import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  fetchUnitById,
  fetchUnitByIdSuccess,
  fetchUnitByIdFailure,
  addUnit,
  addUnitSuccess,
  addUnitFailure,
  updateUnit,
  updateUnitSuccess,
  updateUnitFailure,
  deleteUnit,
  deleteUnitSuccess,
  deleteUnitFailure,
  bulkDeleteUnits,
  bulkDeleteUnitsSuccess,
  bulkDeleteUnitsFailure,
} from './slice';

/**
 * Extract error message from various backend error formats
 */
function extractErrorMessage(error) {
  if (!error) return null;

  // If error is a string, return it
  if (typeof error === 'string') return error;

  // If error has message property
  if (error.message) return error.message;

  // If error has errors object (validation errors)
  if (error.errors) {
    // Handle { errors: { Code: ["message"], Name: ["message"] } }
    if (typeof error.errors === 'object' && !Array.isArray(error.errors)) {
      const firstKey = Object.keys(error.errors)[0];
      if (firstKey && Array.isArray(error.errors[firstKey])) {
        return error.errors[firstKey][0];
      }
    }
    // Handle { errors: ["message"] }
    if (Array.isArray(error.errors) && error.errors.length > 0) {
      return error.errors[0];
    }
  }

  return null;
}

/**
 * Translate error message from backend
 * Maps common backend error messages to i18n translation keys
 */
function translateError(error) {
  const errorMessage = extractErrorMessage(error);

  // Debug: log the actual error to see what backend returns
  console.log('Unit error:', error, 'Extracted message:', errorMessage);

  if (!errorMessage) return i18n.t('units.errors.unknown');

  // Common error message mappings (case-insensitive)
  const errorMap = {
    // Code duplicate errors
    'unit code already exists': 'units.errors.codeExists',
    'code already exists': 'units.errors.codeExists',
    'duplicate code': 'units.errors.codeExists',
    'code is already': 'units.errors.codeExists',
    'the code field': 'units.errors.codeExists',
    'mã đã tồn tại': 'units.errors.codeExists',
    'mã đơn vị đã tồn tại': 'units.errors.codeExists',
    // Name duplicate errors  
    'unit name already exists': 'units.errors.nameExists',
    'name already exists': 'units.errors.nameExists',
    'duplicate name': 'units.errors.nameExists',
    'name is already': 'units.errors.nameExists',
    'the name field': 'units.errors.nameExists',
    'tên đã tồn tại': 'units.errors.nameExists',
    'tên đơn vị đã tồn tại': 'units.errors.nameExists',
    // Request validation errors
    'the request field is required': 'units.errors.addFailed',
    'request field is required': 'units.errors.addFailed',
    // Other errors
    'unit not found': 'units.errors.unitNotFound',
    'not found': 'units.errors.unitNotFound',
    'failed to add unit': 'units.errors.addFailed',
    'failed to update unit': 'units.errors.updateFailed',
    'failed to delete unit': 'units.errors.deleteFailed',
    'failed to fetch units': 'units.errors.fetchFailed',
    'failed to fetch init data': 'units.errors.initFailed',
  };

  const lowerMessage = errorMessage.toLowerCase();

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (lowerMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

// Worker saga: Fetch init data
function* handleFetchInit() {
  try {
    const response = yield call(api.get, config.endpoints.units.init);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchInitFailure(translatedError));
  }
}

// Worker saga: Fetch units
function* handleFetchUnits(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, and filterName from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterName } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.name = filterName;
    }

    const response = yield call(api.get, config.endpoints.units.list, params);
    // Pass the whole response (including pagination info)
    yield put(fetchUnitsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchUnitsFailure(translatedError));
  }
}

// Worker saga: Fetch unit by ID
function* handleFetchUnitById(action) {
  try {
    const unitId = action.payload;
    const response = yield call(api.get, `${config.endpoints.units.list}/${unitId}`);
    yield put(fetchUnitByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchUnitByIdFailure(translatedError));
  }
}

// Worker saga: Add unit
function* handleAddUnit(action) {
  try {
    const response = yield call(api.post, config.endpoints.units.create, action.payload);
    yield put(addUnitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(addUnitFailure(translatedError));
  }
}

// Worker saga: Update unit
function* handleUpdateUnit(action) {
  try {
    const { id } = action.payload;
    // Include id in the request body
    const response = yield call(api.put, `${config.endpoints.units.update}/${id}`, action.payload);
    yield put(updateUnitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(updateUnitFailure(translatedError));
  }
}

// Worker saga: Delete unit
function* handleDeleteUnit(action) {
  try {
    const unitId = action.payload;
    yield call(api.delete, `${config.endpoints.units.delete}/${unitId}`);
    yield put(deleteUnitSuccess(unitId));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(deleteUnitFailure(translatedError));
  }
}

// Worker saga: Bulk delete units
function* handleBulkDeleteUnits(action) {
  try {
    const unitIds = action.payload;
    // Call API DELETE /units?ids=1&ids=2&ids=3
    // Use custom paramsSerializer to format array as repeated params
    yield call(api.delete, config.endpoints.units.delete, {
      params: { ids: unitIds },
      paramsSerializer: {
        indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
      },
    });
    yield put(bulkDeleteUnitsSuccess(unitIds));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(bulkDeleteUnitsFailure(translatedError));
  }
}

// Watcher saga
export function* watchUnitsActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchUnits.type, handleFetchUnits);
  yield takeLatest(fetchUnitById.type, handleFetchUnitById);
  yield takeLatest(addUnit.type, handleAddUnit);
  yield takeLatest(updateUnit.type, handleUpdateUnit);
  yield takeLatest(deleteUnit.type, handleDeleteUnit);
  yield takeLatest(bulkDeleteUnits.type, handleBulkDeleteUnits);
}
